import React, { useCallback, useContext, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Flyout, FlyoutTitle } from 'components/Flyout';
import { FlyoutContext } from 'components/Flyout/FlyoutContext';
import Message from 'components/Message';
import { createErrorSnackbar, createSuccessSnackbar } from 'components/Snackbar';
import { bem } from 'helpers/class-name';
import { selectActiveVenue } from 'selectors/general';
import { selectLocale } from 'selectors/locale-data';
import { selectIsMobile } from 'selectors/ui';

import { getIsTaxExcludedMenuPrice } from 'constants/logic';
import { openModal } from 'components/Modal';
import {
  getCategoryId,
  getCategoryMenuId,
  getItemCategoryIds,
  getItemId,
  getItemName,
  getItemPayoutRate,
} from '../SkuMenuData.normalizers';
import { getErrorSnackbarDescriptionWithContactLink } from '../SkuErrorSnackbar.helpers';
import useSkuCategories from '../useSkuCategories';
import useSkuMutations from '../useSkuMutations';
import useSkuPricingSubmission from '../useSkuPricingSubmission';
import { ADD_CATEGORY_MODAL_NAME } from './AddCategoryModal';
import SkuEditItemFormContent from './SkuEditItemFormContent';
import { messages } from './SkuEditItemForm.messages';

import './SkuItemForm.scss';
import './SkuAddItemForm.scss';

import { STATUS_TO_BOOLEANS } from '../SkuItems.constants';

const { block } = bem('item-form');

export const SKU_EDIT_ITEM_FLYOUT_ID = 'sku-edit-item-flyout';

const SkuEditItemForm = ({
  isLoading,
  currencyCode,
  item,
  preselectedCategoryIds,
  categoriesRefreshKey,
  onSuccess,
  isReadOnly,
}) => {
  const dispatch = useDispatch();
  const isMobile = useSelector(selectIsMobile);
  const locale = useSelector(selectLocale);
  const venue = useSelector(selectActiveVenue);
  const { closeFlyout } = useContext(FlyoutContext);

  const { venueId, countryCodeShort } = venue;
  const itemId = getItemId(item);
  const itemName = getItemName(item);
  const payoutRate = getItemPayoutRate(item);
  const [localRefreshKey, setLocalRefreshKey] = useState(0);

  const baseCategoryIds = useMemo(() => {
    if (preselectedCategoryIds?.length) {
      return preselectedCategoryIds;
    }
    return getItemCategoryIds(item);
  }, [item, preselectedCategoryIds]);

  const { groups, subgroups, categoriesLoading, preselectedCategories } = useSkuCategories(
    venueId,
    baseCategoryIds,
    categoriesRefreshKey + localRefreshKey
  );

  const isTaxExcludedMenuPrice = getIsTaxExcludedMenuPrice(countryCodeShort);
  const menuPriceDescription = isTaxExcludedMenuPrice
    ? messages.taxExcludedMenuPriceDescription
    : messages.taxIncludedMenuPriceDescription;

  const formKey = useMemo(
    () => `${itemId || 'no-item'}::${(baseCategoryIds || []).join(',')}::${subgroups.length}`,
    [itemId, baseCategoryIds, subgroups.length]
  );

  const { editItem } = useSkuMutations({ venueId });

  const { handleSubmitPricing } = useSkuPricingSubmission({
    venue,
    currencyCode,
    onSuccess,
    errorLabel: 'SKU Item edit',
  });

  const handleAddCategory = useCallback(() => {
    const defaultMenuId = baseCategoryIds?.length
      ? getCategoryMenuId(subgroups.find(s => getCategoryId(s) === baseCategoryIds[0]))
      : undefined;

    dispatch(
      openModal(ADD_CATEGORY_MODAL_NAME, {
        menus: groups,
        venueId,
        defaultMenuId,
        onSuccess: () => setLocalRefreshKey(prev => prev + 1),
      })
    );
  }, [dispatch, groups, venueId, baseCategoryIds, subgroups]);

  const handleSubmit = useCallback(
    async values => {
      const {
        itemName: nextName,
        itemDescription: nextDescription,
        categories,
        availabilityStatus: nextAvailabilityStatus,
        menuPrice: price,
        linkToMenu: link,
      } = values;
      const categoryEntries = Array.isArray(categories) ? categories : preselectedCategories;

      const { enabled, soldOut, partnerState } = STATUS_TO_BOOLEANS[nextAvailabilityStatus] || {
        enabled: true,
        soldOut: false,
        partnerState: null,
      };

      try {
        await editItem.mutateAsync({
          itemId,
          optimistic: {
            name: nextName.trim(),
            description: nextDescription.trim(),
            enabled,
            soldOut,
            partnerState,
          },
          payload: {
            name: nextName.trim(),
            description: nextDescription.trim(),
            enabled,
            soldOut,
            partnerState,
          },
          baseCategoryIds,
          nextCategories: categoryEntries,
        });

        if (price && link) {
          const pricingSucceeded = await handleSubmitPricing({ name: nextName, price, link });
          if (!pricingSucceeded) return;
        }

        onSuccess?.({
          itemId,
          itemName: nextName,
          menuPrice: price,
          linkToMenu: link,
        });
        closeFlyout();
        dispatch(
          createSuccessSnackbar(
            messages.successEditItem,
            {
              itemName: nextName,
              strong: chunks => <strong>{chunks}</strong>,
            },
            { isLowStyle: true }
          )
        );
      } catch {
        closeFlyout();
        dispatch(
          createErrorSnackbar(
            messages.errorEditItem,
            { strong: chunks => <strong>{chunks}</strong> },
            { isLowStyle: true },
            getErrorSnackbarDescriptionWithContactLink(messages.errorEditItemDescription)
          )
        );
      }
    },
    [preselectedCategories, editItem, handleSubmitPricing, onSuccess, itemId, baseCategoryIds, closeFlyout, dispatch]
  );

  return (
    <Flyout
      id={SKU_EDIT_ITEM_FLYOUT_ID}
      className={`${block()} sku-add-item-flyout sku-edit-item-flyout`}
      onClose={closeFlyout}
      overrideOutsideClick={() => true}
      showBackButton={isMobile}>
      <FlyoutTitle>
        <Message message={isReadOnly ? messages.viewItem : messages.editItem} values={{ itemName }} />
      </FlyoutTitle>
      <SkuEditItemFormContent
        isReadOnly={isReadOnly}
        isLoading={isLoading}
        categoriesLoading={categoriesLoading}
        groups={groups}
        subgroups={subgroups}
        currencyCode={currencyCode}
        locale={locale}
        item={item}
        itemName={itemName}
        payoutRate={payoutRate}
        preselectedCategories={preselectedCategories}
        menuPriceDescription={menuPriceDescription}
        formKey={formKey}
        onSubmit={handleSubmit}
        onAddCategory={handleAddCategory}
      />
    </Flyout>
  );
};

SkuEditItemForm.propTypes = {
  isLoading: PropTypes.bool,
  currencyCode: PropTypes.string.isRequired,
  item: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    description: PropTypes.string,
    menuPrice: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    linkToMenu: PropTypes.string,
    enabled: PropTypes.bool,
    soldOut: PropTypes.bool,
  }),
  preselectedCategoryIds: PropTypes.arrayOf(PropTypes.string),
  categoriesRefreshKey: PropTypes.number,
  onSuccess: PropTypes.func,
  isReadOnly: PropTypes.bool,
};

SkuEditItemForm.defaultProps = {
  isLoading: false,
  item: null,
  preselectedCategoryIds: [],
  categoriesRefreshKey: 0,
  onSuccess: null,
};

export default SkuEditItemForm;
